import { NumberSource } from '@lmt/shared-types';
import { ResourceType, VisitDto } from '@lmt/shared-types';
import {
  Field,
  FieldProps,
} from '@lmt/unicorn-shared/app/components/common/Field';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { TagsViewer } from '@lmt/unicorn-shared/app/components/common/ui-kit/tags/TagsViewer';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { getFormattedDateTimeRange } from '@lmt/unicorn-shared/app/components/patients/common';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentsList } from '../documents/DocumentsList';

import styles from './VisitPreview.module.scss';
import { ExternalPatientNumberBadge } from '../../../../ExternalPatientNumberBadge';
import { useSortedResources } from '@lmt/unicorn-shared/app/api/custom-hooks';
import clsx from 'clsx';
import { VisitWithDoctor } from '../../types';
import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';

interface VisitPreviewProps {
  visit: VisitWithDoctor;
  dataBlockId?: number;
  isReadOnly?: boolean;
}

const StyledField = (props: React.PropsWithChildren<FieldProps>) => (
  <Field className={styles.field} {...props}>
    <div className={styles.text} data-test-id="field-value">
      {props.children}
    </div>
  </Field>
);

export const VisitPreview: FC<VisitPreviewProps> = (props) => {
  const { visit, dataBlockId, isReadOnly } = props;

  const i18next = useTranslation();
  const forms = useGlobalForms();

  const visitTypes = useVisitTypes();

  const places = useSortedResources(
    ResourceType.Places,
    true,
    visit.resourceIds ?? [],
  );
  const doctors = useSortedResources(
    ResourceType.Doctors,
    true,
    visit.resourceIds ?? [],
  );
  const specialConditions = useSortedResources(
    ResourceType.SpecialConditions,
    true,
    visit.resourceIds ?? [],
  );

  const externalNumberAsLinkProps = useMemo(
    () => ({
      title: <ExternalPatientNumberBadge />,
    }),
    [],
  );

  const visitInfo = (
    <div className={styles.visitInfoContainer}>
      <div className={styles.columns}>
        <div className={styles.column}>
          <StyledField
            title={i18next.t('patients.cases.visits.fields.number')}
            linkProps={
              visit.numberSource === NumberSource.External
                ? externalNumberAsLinkProps
                : undefined
            }
          >
            {visit.number}
          </StyledField>
          <StyledField
            title={i18next.t('patients.cases.visits.fields.attending_doctor')}
          >
            <div className={styles.resourceField}>
              <span>
                {visit.attendingDoctor
                  ? getDoctorFullName(visit.attendingDoctor)
                  : '-'}
              </span>
              {visit.attendingDoctor?.isDeleted && (
                <div className={styles.deletedResourceLabel}>
                  <span>{i18next.t('event.visit.deleted_resource_label')}</span>
                </div>
              )}
            </div>
          </StyledField>
          <StyledField
            title={i18next.t('patients.cases.visits.fields.datetime')}
          >
            {getFormattedDateTimeRange(visit.start, visit.end)}
          </StyledField>
          <StyledField title={i18next.t('patients.cases.visits.fields.status')}>
            {i18next.t(`visit_status.${visit.statusId}`)}
          </StyledField>
        </div>
        <div className={styles.column}>
          <StyledField title={i18next.t('patients.cases.visits.fields.places')}>
            <div className={styles.resourceField}>
              <span>{places[0]?.name || '-'}</span>
              {places[0]?.isDeleted && (
                <div className={styles.deletedResourceLabel}>
                  <span>{i18next.t('event.visit.deleted_resource_label')}</span>
                </div>
              )}
            </div>
          </StyledField>
          <StyledField title={i18next.t('patients.cases.visits.fields.doctor')}>
            <div className={styles.resourceField}>
              <span>{doctors[0]?.name || '-'}</span>
              {doctors[0]?.isDeleted && (
                <div className={styles.deletedResourceLabel}>
                  <span>{i18next.t('event.visit.deleted_resource_label')}</span>
                </div>
              )}
            </div>
          </StyledField>
          <StyledField
            title={i18next.t('patients.cases.visits.fields.special_conditions')}
          >
            {specialConditions.length
              ? specialConditions.map((resource) => (
                  <div className={styles.resourceField}>
                    <span>{resource.name}</span>
                    {resource.isDeleted && (
                      <div className={styles.deletedResourceLabel}>
                        <span>
                          {i18next.t('event.visit.deleted_resource_label')}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              : '-'}
          </StyledField>
        </div>
      </div>
      <div className={styles.comment}>
        <StyledField title={i18next.t('patients.cases.visits.fields.note')}>
          <TagsViewer text={visit.note || '-'} />
        </StyledField>
      </div>
    </div>
  );

  return (
    <div className={styles.container} data-test-id="visit-block">
      <div className={styles.header}>
        <BlockTitle
          title={i18next.t('patients.cases.visits.title', {
            visit: i18next.t(
              (visitTypes.find((visitType) => visitType.id === visit.typeId)
                ?.value || '-') as any,
            ),
            date: getFormattedDateTimeRange(visit.start, visit.end),
          })}
        />
      </div>
      <div className={styles.dataContainer}>
        <div className={styles.recordsContainer}>
          <div className={styles.recordsHeader}>
            <div className={styles['header-column']}>
              <div className={styles.recordsTitle}>
                {i18next.t('patients.cases.records')}
              </div>
            </div>
            <div className={styles['header-column']}>
              <div className={clsx(styles.recordsTitle, styles.column)}>
                {i18next.t('patients.cases.visits.resources')}
              </div>
              <div className={styles.recordsOptions}>
                {!isReadOnly && (
                  <UniLink
                    className={styles.link}
                    color={ButtonColor.Primary}
                    title={i18next.t('patients.cases.edit_button')}
                    onClick={() => forms.openVisitEditing(visit.id)}
                  />
                )}
              </div>
            </div>
          </div>

          {visitInfo}
        </div>
        <div className={styles.documentContainer}>
          {dataBlockId && (
            <DocumentsList
              patientId={props.visit.patientId}
              dataBlockId={dataBlockId}
              visitId={props.visit.id}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface Props {
  title: string;
}

const BlockTitle: FC<Props> = (props) => (
  <div className={styles.title} data-test-id="block-title">
    {props.title}
  </div>
);
